import {
  Type,
  inject,
  ViewContainerRef,
  reflectComponentType,
  effect,
  OutputEmitterRef,
  Provider,
  ModelSignal,
  type Signal,
  Injector,
  untracked,
  type ComponentRef,
} from '@angular/core';

type AbstractType<T = any> = abstract new (...args: any[]) => T;
type MultipleComponents<T> = { id: string; componentClass: Type<T> };

export const renderComponentContract = (
  contract: AbstractType<any>,
  selfInstance: {
    componentId?: Signal<string>;
    [key: string]: any;
  },
) => {
  const componentsData: Type<any> | Array<MultipleComponents<any>> = inject(contract, {
    skipSelf: true,
  });
  const vcRef = inject(ViewContainerRef);
  const contractMirror = reflectComponentType(<Type<any>>contract)!;
  const injector = inject(Injector);

  const render = (ref: ComponentRef<any>) => {
    const inputMap = Object.fromEntries(
      contractMirror.inputs.map(inputDef => [inputDef.propName, inputDef]),
    );

    const destroyFns = [
      ...contractMirror.inputs.map(inputDef => {
        if (!inputDef.isSignal) {
          throw new Error('Only signals are supported');
        }

        const effectRef = effect(
          () => ref.setInput(inputDef.templateName, selfInstance[inputDef.propName]()),
          { injector },
        );
        return () => effectRef.destroy();
      }),
      ...contractMirror.outputs.map(outputDef => {
        const isModel = inputMap[outputDef.propName];
        if (isModel) {
          const selfModel: ModelSignal<any> = selfInstance[outputDef.propName];
          const refModel: ModelSignal<any> = ref.instance[outputDef.propName];
          const sub = refModel.subscribe(value => selfModel.set(value));
          return () => sub.unsubscribe();
        } else {
          const selfOutput: OutputEmitterRef<any> = selfInstance[outputDef.propName];
          const refOutput: OutputEmitterRef<any> = ref.instance[outputDef.propName];
          const sub = refOutput.subscribe(value => selfOutput.emit(value));
          return () => sub.unsubscribe();
        }
      }),
      () => ref.destroy(),
    ];

    return { destroyFns };
  };

  effect(onCleanup => {
    if (Array.isArray(componentsData) && !selfInstance.componentId) {
      throw new Error('componentId is required when multiple components are provided.');
    }
    const componentId = selfInstance.componentId?.();
    const componentType =
      Array.isArray(componentsData) && componentId
        ? componentsData.find(({ id }) => componentId === id)?.componentClass
        : <Type<any>>componentsData;
    if (!componentType) {
      throw new Error(`Component with id "${componentId}" not found in contract data.`);
    }

    const ref = vcRef.createComponent<Record<string, any>>(componentType);
    const { destroyFns } = untracked(() => render(ref));
    onCleanup(() => {
      destroyFns.forEach(fn => fn());
    });
  });
};

export const provideComponentContract = <T, M extends boolean>(
  contract: AbstractType<T>,
  component: M extends false ? Type<T> : MultipleComponents<T>,
  multi: M,
): Provider => ({ provide: contract, useValue: component, multi });
